import Loader from '@repo/ui/loader'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { getPlaceDetails } from '../../../api/places/get-place-details'
import { getPlaces } from '../../../api/places/get-places'
import { useNativeCountryName } from '../../../lib/hooks/use-native-country-name'
import { Place } from '../../../lib/types/models/place/place'
import { PlaceDetails } from '../../../lib/types/models/place/place-detail'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { useFormField } from '../forms/form'

type AddressInputProps = {
  label: string
  value?: string
  errorMessage?: string
  required?: boolean
  disabled?: boolean
  /** Shows the country of the suggestions and of the selected place in the country's own language */
  nativeCountryNames?: boolean
  onChange: (value: string) => void
  onDetailsChange?: (placeDetails: PlaceDetails) => void
}

const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  onDetailsChange,
  disabled,
  nativeCountryNames,
  ...props
}) => {
  const nativeCountryName = useNativeCountryName()
  const [search, setSearch] = useState('')
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['places', search],
    queryFn: () => getPlaces(search),
    enabled: false
  })

  useEffect(() => {
    if (!search.trim()) return

    const timer = setTimeout(() => {
      refetch()
    }, 500)

    return () => {
      clearTimeout(timer)
    }
  }, [search, refetch])

  const getPlaceLabel = ({ description, terms }: Place) => {
    const country = terms.at(-1)?.value
    if (!nativeCountryNames || !country || !description.endsWith(country)) return description
    return description.slice(0, -country.length) + nativeCountryName(country)
  }

  const handleChange = async (option: string) => {
    onChange(option)
    if (onDetailsChange) {
      const place = data?.find((place) => place.description === option)
      if (!place) return

      setIsLoadingDetails(true)
      try {
        const placeDetails = await getPlaceDetails(place.place_id)
        onDetailsChange(
          nativeCountryNames
            ? { ...placeDetails, country: nativeCountryName(placeDetails.country) ?? '' }
            : placeDetails
        )
      } catch (error) {
        console.error('Error fetching place details:', error)
      } finally {
        setIsLoadingDetails(false)
      }
    }
  }

  return (
    <div className="relative">
      <SelectInput<string>
        hideDropdownIcon
        {...props}
        searchable
        allowNewOption
        options={data?.map((place) => ({
          label: getPlaceLabel(place),
          value: place.description
        }))}
        selectedOption={value ? getSelectedOption(value, value) : undefined}
        onChange={handleChange}
        onTextChange={setSearch}
        disabled={disabled || isLoadingDetails}
      />
      {(isLoading || isLoadingDetails) && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <Loader size="xs" />
        </div>
      )}
    </div>
  )
}

export default AddressInput

export const ControlledAdressInput: React.FC<AddressInputProps> = (props) => {
  const { error } = useFormField()
  return <AddressInput {...props} errorMessage={error ? String(error?.message) : undefined} />
}
